import React, { useEffect, useState, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import api from "service/api";
import { toast } from "react-toastify";
import Select from "react-select";
import { CookieTool } from "utils/CookieTool";

function GenerativeAnalysisFields({ back }) {
  const { watch, setValue, control, formState } = useFormContext();
  const { errors, isSubmitted } = formState;

  const permissions = CookieTool.getLarge("permissions");
  const [segments, setSegments] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  const selectedClient = watch("client");
  const selectedSegment = watch("segment");
  const isCopy = watch("IsCopy");

  useEffect(() => {
    if (selectedClient && selectedClient.id) {
      setLoadingSegments(true);
      api
        .get(
          `/segments?page=1&client=${selectedClient.id}&limit=${new Date().getTime()}`,
        )
        .then((response) => response.data)
        .then((response) => {
          const segmentOptions = response.segments.map((item) => ({
            label: item.title,
            value: item.id,
            id: item.id,
            name: item.title,
          }));
          setSegments(segmentOptions);

          if (!isCopy) {
            setValue("segment", null);
            setValue("prompts", []);
          }
        })
        .catch(() => {
          toast.error("Erro ao recuperar segmentos!");
        })
        .finally(() => setLoadingSegments(false));
    }
  }, [selectedClient, setValue, isCopy]);

  const fetchPrompts = useCallback(() => {
    if (selectedSegment && selectedSegment.id) {
      setLoadingPrompts(true);
      api
        .get(
          `/prompts?segmentId=${selectedSegment.id}&limit=${new Date().getTime()}`,
        )
        .then((res) => {
          const promptOptions = res.data.prompts.map((prompt) => ({
            value: prompt.id,
            label: prompt.title,
          }));
          setPrompts(promptOptions);
          if (!isCopy) {
            setValue(
              "prompts",
              promptOptions.map((p) => p.value),
            );
          } else {
            const currentPrompts = watch("prompts") || [];
            const validPrompts = currentPrompts.filter((promptId) =>
              promptOptions.some((option) => option.value === promptId),
            );

            if (validPrompts.length === 0 && promptOptions.length > 0) {
              setValue(
                "prompts",
                promptOptions.map((p) => p.value),
              );
            } else if (validPrompts.length !== currentPrompts.length) {
              setValue("prompts", validPrompts);
            }
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar prompts:", error);
          toast.error("Erro ao carregar prompts!");
        })
        .finally(() => setLoadingPrompts(false));
    } else {
      setPrompts([]);
    }
  }, [selectedSegment, setValue, isCopy, watch]);

  useEffect(() => {
    fetchPrompts();
    if (!selectedSegment || !selectedSegment.id) {
      setPrompts([]);
    }
  }, [selectedSegment, fetchPrompts]);

  const handleCreatePrompt = () => {
    if (selectedSegment && selectedSegment.id) {
      window.open(
        `/admin/prompts-form?segmentId=${selectedSegment.id}`,
        "_blank",
      );
    }
  };

  const handleRefreshPrompts = () => {
    fetchPrompts();
  };

  return (
    <>
      <div className="mb-3">
        <label className="form-control-label">Segmento</label>
        <Controller
          name="segment"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={segments}
              isLoading={loadingSegments}
              placeholder={
                loadingSegments
                  ? "Carregando segmentos..."
                  : "Selecione um segmento"
              }
              isDisabled={!selectedClient || loadingSegments}
              onChange={(option) =>
                field.onChange(
                  option ? { id: option.id, name: option.name } : null,
                )
              }
              value={segments.find((opt) => opt.id === field.value?.id) || null}
              isClearable
            />
          )}
        />
        {isSubmitted && errors.segment && (
          <small style={{ color: "red" }}>{errors.segment.message}</small>
        )}
      </div>
      <div className="mb-3">
        <div className="d-flex align-items-center mb-1">
          <label className="form-control-label mb-0 mr-2">Prompts</label>
          {permissions.includes("create:prompt") && (
            <button
              type="button"
              className="btn btn-sm btn-primary ml-2"
              onClick={handleCreatePrompt}
              disabled={!selectedSegment || !selectedSegment.id}
              title="Criar novo prompt"
              style={{ width: "30px", height: "30px", padding: 0 }}
            >
              +
            </button>
          )}
          {selectedSegment && selectedSegment.id && (
            <button
              type="button"
              className="btn btn-sm btn-primary ml-2"
              onClick={handleRefreshPrompts}
              disabled={!selectedSegment || loadingPrompts}
              title="Atualizar prompts"
              style={{ width: "30px", height: "30px", padding: 0 }}
            >
              &#x21bb;
            </button>
          )}
        </div>
        <Controller
          name="prompts"
          control={control}
          render={({ field }) => {
            const selectedValues = prompts.filter(
              (opt) =>
                Array.isArray(field.value) && field.value.includes(opt.value),
            );

            return (
              <Select
                {...field}
                options={prompts}
                isMulti
                isLoading={loadingPrompts}
                placeholder={
                  loadingPrompts
                    ? "Carregando prompts..."
                    : "Selecione os prompts"
                }
                isDisabled={!selectedSegment || loadingPrompts}
                onChange={(options) =>
                  field.onChange(options ? options.map((opt) => opt.value) : [])
                }
                value={selectedValues}
                closeMenuOnSelect={false}
                noOptionsMessage={() =>
                  loadingPrompts
                    ? "Carregando prompts..."
                    : "Nenhum prompt disponível"
                }
              />
            );
          }}
        />
        {isSubmitted && errors.prompts && (
          <small style={{ color: "red" }}>{errors.prompts.message}</small>
        )}
      </div>
    </>
  );
}

export default GenerativeAnalysisFields;
